import { CheckCircle, X, XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CREATE_STUDENT } from "../../appSync/mutations";
import styles from "./EnrollmentForm.module.css";

const EnrollmentForm = ({
  isOpen,
  onClose,
  selectedProgram,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedProgram: string;
}) => {
  const initialFormState = {
    firstName: "",
    lastName: "",
    email: "",
    cellPhone: "",
    guardianName: "",
    courseType: "",
    classInterested: "",
    hasExperience: "",
    experienceDetails: "",
    dob: "",
    gender: "",
    street: "",
    city: "",
    state: "",
    zipcode: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [agreed, setAgreed] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [errorMessage] = useState<string | null>(null);

  console.log(`Selected program ${selectedProgram}`);

  useEffect(() => {
    if (selectedProgram) {
      setFormData((prev) => ({
        ...prev,
        classInterested: selectedProgram,
      }));
    }
  }, [selectedProgram]);

  useEffect(() => {
    // Add class to body when form is mounted/visible
    document.body.classList.add("no-touch");

    return () => {
      // Remove class when form is closed
      document.body.classList.remove("no-touch");
    };
  }, []);

  // Helper to reset and close
  const handleClose = () => {
    setIsSubmitted(false);
    setFormData(initialFormState);
    setErrors({});
    setAgreed(false);
    onClose();
  };

  const handleReset = () => {
    setFormData(initialFormState);
    setErrors({});
    setAgreed(false);
    if (selectedProgram) {
      setFormData((prev) => ({
        ...prev,
        classInterested: selectedProgram,
      }));
    }
  };

  const formatPhoneNumber10 = (value: string) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return digits;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePhoneFormat = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const formattedValue = formatPhoneNumber10(value);
    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) newErrors.firstName = "Required";
    if (!formData.lastName.trim()) newErrors.lastName = "Required";

    const rawCell = formData.cellPhone.replace(/-/g, "");
    if (rawCell.length !== 10) newErrors.cellPhone = "Must be 10 digits";

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    if (isMinor && !formData.guardianName?.trim()) {
      newErrors.guardianName =
        "Guardian name is required for students under 18";
    }
    if (!formData.classInterested) {
      newErrors.classInterested = "Please select a class";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!validate()) {
      // General form-level error
      setErrors((prev) => ({ ...prev, form: "There are errors in the form" }));

      // Scroll modal to top
      const modal = document.querySelector(`.${styles.modal}`);
      if (modal) {
        modal.scrollTop = 0;
      }

      setLoading(false);
      return;
    }

    const inputPayload = { ...formData };

    try {
      const response = await fetch(process.env.REACT_APP_APPSYNC_URL!, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_APPSYNC_API_KEY!,
        },
        body: JSON.stringify({
          query: CREATE_STUDENT,
          variables: { input: inputPayload },
        }),
      });

      const result = await response.json();
      const createStudent = result.data?.createStudent;

      if (!createStudent) {
        setErrors({ form: "Unexpected server response" });
        setLoading(false);
        return;
      }

      if (!createStudent.success) {
        // Map backend message to individual fields if possible
        const backendErrors: Record<string, string> = {};

        // Example parsing logic if message lists multiple fields
        if (createStudent.message.includes("Email")) {
          backendErrors.email = createStudent.message;
        }
        // Fallback general error
        if (Object.keys(backendErrors).length === 0) {
          backendErrors.form = createStudent.message;
        }

        // backendErrors.form += " (e.g. " + createStudent.message + ")";
        setErrors(backendErrors);
        // Scroll to top of modal
        const modal = document.querySelector(`.${styles.modal}`);
        if (modal) {
          modal.scrollTop = 0;
        }

        setLoading(false);
        return;
      }

      // Success
      setSuccess(true);
      setIsSubmitted(true);
      setErrors({});
    } catch (err: any) {
      setErrors({ form: "Network or server error. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Inside your component:
  const calculateAge = (dob: string) => {
    if (!dob) return 100; // Default to adult if no date
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const isMinor = calculateAge(formData.dob) < 18;

  const getFiveYearsAgo = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 5);
    return today.toISOString().split("T")[0];
  };

  const handleNameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isControlKey = event.key.length > 1;
    const isLetter = /^[a-zA-Z\s]$/.test(event.key);
    if (!isControlKey && !isLetter) event.preventDefault();
  };

  const handlePhoneKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isControlKey = event.key.length > 1;
    const isDigit = /^[0-9]$/.test(event.key);
    if (!isControlKey && !isDigit) event.preventDefault();
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.classList.add("modal-open");
    } else {
      document.body.style.overflow = "unset";
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.style.overflow = "unset";
      document.body.classList.remove("modal-open");
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const renderModal = () => {
    if (isSubmitted && isSuccess) {
      return (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            {selectedProgram ? (
              <>
                <div className={styles.modalHeader}>
                  <div className={styles.iconCircle}>
                    <span className={styles.checkIcon}>✓</span>
                  </div>
                </div>

                <div className={styles.modalBody}>
                  <h2>Registration Successful!</h2>
                  <div className={styles.divider}></div>

                  {formData.classInterested.includes("Summer") ? (
                    <div className={styles.campSuccessDetails}>
                      <p className={styles.welcomeText}>
                        Get your brushes ready,{" "}
                        <strong>{formData.firstName}</strong>!
                      </p>

                      <div className={styles.infoCard}>
                        <div className={styles.infoRow}>
                          <span className={styles.label}>Event</span>
                          <span className={styles.value}>
                            Summer Art Camp 2026
                          </span>
                        </div>
                        <div className={styles.infoRow}>
                          <span className={styles.label}>Start Date</span>
                          <span className={styles.value}>July 06, 2026</span>
                        </div>
                      </div>

                      <p className={styles.followUp}>
                        Check your inbox at <span>{formData.email}</span> for
                        your welcome pack and supply list.
                      </p>
                    </div>
                  ) : (
                    <div className={styles.simpleThankYou}>
                      <h3 className={styles.gradientHeading}>
                        You’re all set!
                      </h3>
                      <p className={styles.subText}>
                        We’ve received your enrollment for{" "}
                        <strong>{formData.classInterested}</strong>.
                      </p>

                      <div className={styles.nextSteps}>
                        <div className={styles.step}>
                          <span className={styles.stepDot}></span>
                          <p>Our team is reviewing your details</p>
                        </div>
                      </div>

                      <p className={styles.footerNote}>
                        A confirmation has been sent to{" "}
                        <em>{formData.email}</em>
                      </p>
                    </div>
                  )}
                </div>

                <button onClick={onClose} className={styles.closeBtn}>
                  Back
                </button>
              </>
            ) : (
              <div className={styles.successWrapper}>
                <CheckCircle size={64} className={styles.successIcon} />
                <h2>Enrollment Received!</h2>
                <p>
                  Thank you, {formData.firstName}. Please check your email for
                  more.
                </p>
                <button onClick={handleClose} className={styles.submitBtn}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (isSubmitted && errors) {
      return (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <div className={styles.errorWrapper}>
              <XCircle size={64} className={styles.errorIcon} />
              <h2>Registration error</h2>
              <p>{errorMessage}</p>
              <button onClick={handleClose} className={styles.submitBtn}>
                Close
              </button>
            </div>
          </div>
        </div>
      );
    }

    // FORM VIEW (default)
    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <button className={styles.closeBtn} onClick={handleClose}>
            <X size={24} />
          </button>

          <header className={styles.header}>
            <h2>Student Enrollment</h2>
            <p>Begin your artistic journey with Nupur Creations</p>
          </header>
          {errors.form && (
            <div className={styles.formError}>
              <strong>{errors.form}</strong>
            </div>
          )}
          <form
            className={styles.formGrid}
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className={styles.column}>
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Personal Details</h3>

                {/* Row 1: Names */}
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label>
                      First Name <span className={styles.requiredStar}>*</span>
                    </label>
                    <input
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      onKeyDown={handleNameKeyDown}
                      className={errors.firstName ? styles.inputError : ""}
                      required
                    />
                    {errors.firstName && (
                      <span className={styles.errorText}>
                        {errors.firstName}
                      </span>
                    )}
                  </div>
                  <div className={styles.field}>
                    <label>
                      Last Name <span className={styles.requiredStar}>*</span>
                    </label>
                    <input
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                      onKeyDown={handleNameKeyDown}
                      className={errors.lastName ? styles.inputError : ""}
                      required
                    />
                    {errors.lastName && (
                      <span className={styles.errorText}>
                        {errors.lastName}
                      </span>
                    )}
                  </div>
                </div>

                {/* Row 2: DOB and Gender */}
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label>
                      Date of Birth{" "}
                      <span className={styles.requiredStar}>*</span>
                    </label>
                    <input
                      type="date"
                      name="dob"
                      required
                      max={getFiveYearsAgo()}
                      value={formData.dob}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>
                      Gender <span className={styles.requiredStar}>*</span>
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Row 3: Guardian (Conditional) - Appears full width if minor */}
                {isMinor && (
                  <div className={styles.field}>
                    <label>
                      Guardian Name{" "}
                      <span className={styles.requiredStar}>*</span>
                    </label>
                    <input
                      name="guardianName"
                      type="text"
                      value={formData.guardianName}
                      onChange={handleChange}
                      required={isMinor}
                    />
                    {errors.guardianName && (
                      <span className={styles.errorText}>
                        {errors.guardianName}
                      </span>
                    )}
                  </div>
                )}
                <p className={styles.highlightBar}>
                  Class is selected for you based on your interest
                </p>
                {/* Row 4: Class Interest and Experience (The New Line) */}
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label>
                      Interested In{" "}
                      <span className={styles.requiredStar}>*</span>
                    </label>
                    <select
                      name="classInterested"
                      value={formData.classInterested}
                      onChange={handleChange}
                      className={selectedProgram ? styles.autoSelected : ""}
                      disabled={!!selectedProgram}
                      required
                    >
                      <option value="">Select a Class</option>
                      <option value="Fine-Arts">Fine Arts</option>
                      <option value="Dance">Dance</option>
                      <option value="Summer Art Camp 2026">
                        Summer Art Camp 2026
                      </option>
                      <option value="Spring Camp 2026">Spring Camp 2026</option>
                    </select>
                    {errors.classInterested && (
                      <span className={styles.errorText}>
                        {errors.classInterested}
                      </span>
                    )}
                  </div>
                </div>

                {/* Conditional Sub-Dropdown for Fine Arts */}
                {formData.classInterested === "Fine-Arts" && (
                  <div className={styles.field}>
                    <label>
                      Fine Arts Medium{" "}
                      <span className={styles.requiredStar}>*</span>
                    </label>
                    <select
                      name="courseType"
                      value={formData.courseType}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Medium</option>
                      <option value="Oil">Oil</option>
                      <option value="Pastel-Art">Pastel-Art</option>
                      <option value="Color-pencil">Color-pencil</option>
                      <option value="Acrylic">Acrylic</option>
                      <option value="Graphite & pencil">
                        Graphite & pencil
                      </option>
                      <option value="Water color">Water color</option>
                    </select>
                  </div>
                )}

                {formData.classInterested === "Dance" && (
                  <div className={styles.field}>
                    <label>
                      Dance Style <span className={styles.requiredStar}>*</span>
                    </label>
                    <select
                      name="courseType"
                      value={formData.courseType}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Dance Style</option>
                      <option value="Contemporary">Contemporary</option>
                      <option value="Hip-Hop">Bharatnatyam</option>
                      <option value="Ballet">Kathak</option>
                      <option value="Jazz">Folk</option>
                    </select>
                  </div>
                )}

                {/* Experience Row */}
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label>
                      Prior Experience?{" "}
                      <span className={styles.requiredStar}>*</span>
                    </label>
                    <div
                      style={{ display: "flex", gap: "15px", marginTop: "8px" }}
                    >
                      <select
                        name="hasExperience"
                        value={formData.hasExperience}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  </div>

                  {/* Experience Details - Only shows if 'yes' is selected */}
                  {formData.hasExperience === "yes" && (
                    <div className={styles.field}>
                      <label>
                        Experience Details{" "}
                        <span className={styles.requiredStar}>*</span>
                      </label>
                      <input
                        name="experienceDetails"
                        type="text"
                        placeholder="Describe your prior art experience"
                        value={formData.experienceDetails}
                        onChange={handleChange}
                        className={
                          errors.experienceDetails ? styles.inputError : ""
                        }
                        required={formData.hasExperience === "yes"}
                      />
                      {errors.experienceDetails && (
                        <span className={styles.errorText}>
                          {errors.experienceDetails}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </section>
            </div>

            <div className={styles.column}>
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Contact Information</h3>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label>
                      Cell Phone<span className={styles.requiredStar}>*</span>
                    </label>
                    <input
                      name="cellPhone"
                      type="tel"
                      value={formData.cellPhone}
                      onChange={handleChange}
                      onBlur={handlePhoneFormat}
                      onKeyDown={handlePhoneKeyDown}
                      maxLength={12}
                      required
                    />
                    {errors.cellPhone && (
                      <span className={styles.errorText}>
                        {errors.cellPhone}
                      </span>
                    )}
                  </div>
                  <div className={styles.field}>
                    <label>
                      Email Address{" "}
                      <span className={styles.requiredStar}>*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                    {errors.email && (
                      <span className={styles.errorText}>{errors.email}</span>
                    )}
                  </div>
                </div>
              </section>
            </div>

            <div className={styles.column}>
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Address Information</h3>
                <div className={styles.field}>
                  <label>
                    Street Address{" "}
                    <span className={styles.requiredStar}>*</span>
                  </label>
                  <input
                    name="street"
                    type="text"
                    value={formData.street}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label>
                      City <span className={styles.requiredStar}>*</span>
                    </label>
                    <input
                      name="city"
                      type="text"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      onKeyDown={handleNameKeyDown}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>
                      State <span className={styles.requiredStar}>*</span>
                    </label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                    >
                      <option value="">--Select State--</option>
                      <option value="AL">Alabama</option>
                      <option value="AK">Alaska</option>
                      <option value="AZ">Arizona</option>
                      <option value="AR">Arkansas</option>
                      <option value="CA">California</option>
                      <option value="CO">Colorado</option>
                      <option value="CT">Connecticut</option>
                      <option value="DE">Delaware</option>
                      <option value="DC">District Of Columbia</option>
                      <option value="FL">Florida</option>
                      <option value="GA">Georgia</option>
                      <option value="HI">Hawaii</option>
                      <option value="ID">Idaho</option>
                      <option value="IL">Illinois</option>
                      <option value="IN">Indiana</option>
                      <option value="IA">Iowa</option>
                      <option value="KS">Kansas</option>
                      <option value="KY">Kentucky</option>
                      <option value="LA">Louisiana</option>
                      <option value="ME">Maine</option>
                      <option value="MD">Maryland</option>
                      <option value="MA">Massachusetts</option>
                      <option value="MI">Michigan</option>
                      <option value="MN">Minnesota</option>
                      <option value="MS">Mississippi</option>
                      <option value="MO">Missouri</option>
                      <option value="MT">Montana</option>
                      <option value="NE">Nebraska</option>
                      <option value="NV">Nevada</option>
                      <option value="NH">New Hampshire</option>
                      <option value="NJ">New Jersey</option>
                      <option value="NM">New Mexico</option>
                      <option value="NY">New York</option>
                      <option value="NC">North Carolina</option>
                      <option value="ND">North Dakota</option>
                      <option value="OH">Ohio</option>
                      <option value="OK">Oklahoma</option>
                      <option value="OR">Oregon</option>
                      <option value="PA">Pennsylvania</option>
                      <option value="RI">Rhode Island</option>
                      <option value="SC">South Carolina</option>
                      <option value="SD">South Dakota</option>
                      <option value="TN">Tennessee</option>
                      <option value="TX">Texas</option>
                      <option value="UT">Utah</option>
                      <option value="VT">Vermont</option>
                      <option value="VA">Virginia</option>
                      <option value="WA">Washington</option>
                      <option value="WV">West Virginia</option>
                      <option value="WI">Wisconsin</option>
                      <option value="WY">Wyoming</option>
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label>
                      Zipcode <span className={styles.requiredStar}>*</span>
                    </label>
                    <input
                      name="zipcode"
                      type="text"
                      value={formData.zipcode}
                      onChange={handleChange}
                      required
                      pattern="\d{5}"
                      onKeyDown={handlePhoneKeyDown}
                    />
                  </div>
                </div>
              </section>
            </div>

            <div className={styles.rulesListWrapper}>
              <h3 className={styles.sectionTitle}>Policy Review</h3>
              <div className={styles.termsGrid}>
                {[
                  "Monthly payment due by 10th after that there will be late payment fees.",
                  "Make up Class needs at least a day's notice",
                  "Without giving any prior notice, taking off from the class will count as absent.",
                  "Cash/paypal/zelle/check payments only.",
                  "No long-term contract, Need a month's/15 days payment at the time of discontinuation.",
                  "You can temporarily suspend classes in case of vacation (Further details available)",
                  "There are 4 classes in a month. 5th class will be considered make-up class.",
                ].map((text, i) => (
                  <label
                    key={i}
                    className={`${styles.fancyCheckbox} ${styles.readOnly}`}
                  >
                    <input type="checkbox" checked readOnly />
                    <span className={styles.checkmark}></span>
                    <span className={styles.labelBody}>{text}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.agreementContainer}>
              <label className={styles.masterCheckboxLabel}>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span className={styles.masterCheckmark}></span>
                <span className={styles.masterText}>
                  I have read and agree to all rules and regulations.
                </span>
              </label>
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.cancelBtn}`}
                onClick={handleClose}
              >
                Cancel
              </button>

              <button
                type="button"
                className={`${styles.actionBtn} ${styles.resetBtn}`}
                onClick={handleReset}
              >
                Reset
              </button>

              <button
                type="submit"
                className={`${styles.actionBtn} ${styles.submitBtn} ${
                  !agreed || loading ? styles.disabled : ""
                }`}
                disabled={!agreed || loading}
              >
                {loading ? "Processing..." : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return createPortal(renderModal(), document.body);
};

export default EnrollmentForm;
